import * as Yup from 'yup';
import i18n from 'i18n';

// Types are owned by ./types.ts (created by another task). We only import them.
import type { Sexe } from './types';

/**
 * INSi trait input validation + accent transliteration.
 *
 * Mirrors the backend trait rules (tech_design_spec §5 / handoff §2.1) so that
 * `searchInsByTraits` calls don't bounce on the wire:
 *
 * | Field          | Rule                                                                                       |
 * |----------------|--------------------------------------------------------------------------------------------|
 * | nomNaissance   | 1..80 chars, uppercase; A-Z/space/'/-; first char A-Z or '; "--" allowed; no doubled OR combined blank/apostrophe; accents transliterated |
 * | prenoms        | 1..10 entries; A-Z/space/'/-; first char A-Z or '; last char ≠ - and ≠ '; "--" forbidden; no doubled blank/apostrophe (combos allowed); index 0 = first usual prénom (RG_INS220) |
 * | sexe           | M or F only (backend rejects I/U)                                                           |
 * | dateNaissance  | YYYY-MM-DD                                                                                  |
 * | lieuNaissance  | optional INSEE code ^[A-Z0-9]{5}$; omitted on first call, added only on CR02 retry          |
 *
 * nom vs prénom divergence per SEL-MP-043 §EF_INS10 (p.25): the doubled hyphen
 * "--" and the last-character restriction differ between the two fields.
 */

// ---------------------------------------------------------------------------
// Allowed characters / regexes (kept in sync with the backend rule table)
// ---------------------------------------------------------------------------

/** A trait (nomNaissance / prénom) may only contain A-Z, space, apostrophe and hyphen. */
export const TRAIT_ALLOWED_CHARS_REGEX = /^[A-Z '-]+$/;

/** The first character of a trait must be A-Z or an apostrophe. */
export const TRAIT_FIRST_CHAR_REGEX = /^[A-Z']/;

/**
 * Prénom only: the last character must be different from hyphen and apostrophe
 * (SEL-MP-043 §EF_INS10 — "Le dernier caractère doit être différent du tiret et
 * de l'apostrophe"). Nom has no such restriction.
 */
export const PRENOM_LAST_CHAR_REGEX = /[^-']$/;

/**
 * Prénom only: a double hyphen is forbidden. The doubled hyphen ("--") is an
 * allowed special character for nomNaissance only (no "at most one" cap on nom).
 */
export const DOUBLE_HYPHEN_REGEX = /--/;

/**
 * Nom only: blank and apostrophe may neither be doubled nor combined
 * (SEL-MP-043 §EF_INS10 — "Blanc et apostrophe ne peuvent être doublés … ou
 * combinés"). Matches any two adjacent chars each being a space or apostrophe,
 * i.e. "  ", "''", "' " and " '".
 */
export const NOM_BLANK_APOSTROPHE_PAIR_REGEX = /[ ']{2}/;

/**
 * Prénom only: blank and apostrophe may not be doubled, but combinations
 * (space+apostrophe) are permitted — prénom is more permissive than nom.
 */
export const PRENOM_DOUBLED_BLANK_OR_APOSTROPHE_REGEX = / {2}|''/;

/** INSEE birthplace code: exactly 5 chars, A-Z + 0-9. */
export const LIEU_NAISSANCE_REGEX = /^[A-Z0-9]{5}$/;

/** Date traits travel as YYYY-MM-DD on the wire. */
export const DATE_NAISSANCE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const NOM_NAISSANCE_MAX = 80;
export const PRENOMS_MAX = 10;

/** Characters allowed verbatim by `transliterateTrait` after diacritic stripping. */
const TRANSLITERATE_DISALLOWED_REGEX = /[^A-Z '-]/g;
/** Unicode combining marks left over after NFD normalization. */
const COMBINING_MARKS_REGEX = /[̀-ͯ]/g;

// ---------------------------------------------------------------------------
// Transliteration util
// ---------------------------------------------------------------------------

/**
 * Normalize a free-text trait into the backend-accepted form:
 *  - uppercase
 *  - strip diacritics via NFD normalization (é→E, ç→C, ï→I, à→A …)
 *  - keep only allowed characters (A-Z, space, apostrophe, hyphen)
 *
 * Note: the apostrophe and hyphen are preserved; everything else (digits,
 * punctuation, residual non-Latin glyphs) is dropped. This is the only mutation
 * applied to user input, and it happens on send (RG_INS210 — pre-filled values
 * stay editable; we never auto-mutate the field the PS is editing).
 */
export const transliterateTrait = (input: string): string => {
  if (!input) {
    return '';
  }

  return input
    .normalize('NFD')
    .replace(COMBINING_MARKS_REGEX, '')
    .toUpperCase()
    .replace(TRANSLITERATE_DISALLOWED_REGEX, '');
};

/** Transliterate a list of prénoms, preserving order (index 0 = first usual). */
export const transliteratePrenoms = (prenoms: string[]): string[] =>
  (prenoms || []).map(transliterateTrait);

// ---------------------------------------------------------------------------
// Field-level Yup validators (composable — first-call vs CR02-retry schemas)
// ---------------------------------------------------------------------------

/**
 * Char-rule validator for `nomNaissance`. Allows the doubled hyphen ("--");
 * forbids doubled/combined blank+apostrophe; no last-character restriction.
 */
const nomStringValidator = (fieldLabel: string): Yup.StringSchema =>
  Yup.string()
    .required(i18n.t('Required'))
    .max(
      NOM_NAISSANCE_MAX,
      i18n.t('Must be at most 80 characters', { field: fieldLabel })
    )
    .matches(TRAIT_ALLOWED_CHARS_REGEX, {
      message: i18n.t(
        'Only uppercase letters, spaces, apostrophes and hyphens are allowed'
      ),
      excludeEmptyString: true,
    })
    .matches(TRAIT_FIRST_CHAR_REGEX, {
      message: i18n.t('Must start with a letter or an apostrophe'),
      excludeEmptyString: true,
    })
    .test(
      'no-doubled-or-combined-blank-apostrophe',
      i18n.t('Spaces and apostrophes cannot be doubled or combined'),
      (value) => !value || !NOM_BLANK_APOSTROPHE_PAIR_REGEX.test(value)
    );

/**
 * Char-rule validator for a single prénom. Stricter than nom: forbids the
 * doubled hyphen, forbids a trailing hyphen/apostrophe, and forbids doubled
 * blank/apostrophe (but allows space+apostrophe combinations).
 */
const prenomStringValidator = (fieldLabel: string): Yup.StringSchema =>
  Yup.string()
    .required(i18n.t('Required'))
    .max(
      NOM_NAISSANCE_MAX,
      i18n.t('Must be at most 80 characters', { field: fieldLabel })
    )
    .matches(TRAIT_ALLOWED_CHARS_REGEX, {
      message: i18n.t(
        'Only uppercase letters, spaces, apostrophes and hyphens are allowed'
      ),
      excludeEmptyString: true,
    })
    .matches(TRAIT_FIRST_CHAR_REGEX, {
      message: i18n.t('Must start with a letter or an apostrophe'),
      excludeEmptyString: true,
    })
    .matches(PRENOM_LAST_CHAR_REGEX, {
      message: i18n.t('Must not end with a hyphen or an apostrophe'),
      excludeEmptyString: true,
    })
    .test(
      'no-double-hyphen',
      i18n.t('A double hyphen is not allowed'),
      (value) => !value || !DOUBLE_HYPHEN_REGEX.test(value)
    )
    .test(
      'no-doubled-blank-apostrophe',
      i18n.t('Spaces and apostrophes cannot be doubled'),
      (value) => !value || !PRENOM_DOUBLED_BLANK_OR_APOSTROPHE_REGEX.test(value)
    );

export const nomNaissanceValidator = (): Yup.StringSchema =>
  nomStringValidator('nomNaissance');

export const prenomsValidator = (): Yup.ArraySchema<string | undefined> =>
  Yup.array()
    .of(prenomStringValidator('prenom'))
    .min(1, i18n.t('At least one first name is required'))
    .max(PRENOMS_MAX, i18n.t('At most 10 first names are allowed'))
    .required(i18n.t('Required'));

export const sexeValidator = (): Yup.StringSchema =>
  Yup.string()
    .oneOf(['M', 'F'] as Sexe[], i18n.t('Sex must be M or F'))
    .required(i18n.t('Required'));

export const dateNaissanceValidator = (): Yup.StringSchema =>
  Yup.string()
    .matches(DATE_NAISSANCE_REGEX, {
      message: i18n.t('Date must be in the format YYYY-MM-DD'),
      excludeEmptyString: true,
    })
    .required(i18n.t('Required'));

/**
 * `lieuNaissance` validator. The field is OPTIONAL by design: it must NEVER be
 * sent on the first `searchInsByTraits` call (EF_INS10_05) and is only added as
 * a disambiguator on a CR02 retry. When present, it must match the INSEE code
 * regex. Use `insiSearchTraitsSchema` for the first call and
 * `insiRetryTraitsSchema` for the CR02 retry (where it is required).
 */
export const lieuNaissanceValidator = (): Yup.StringSchema<
  string | null | undefined
> =>
  Yup.string()
    .matches(LIEU_NAISSANCE_REGEX, {
      message: i18n.t('Birthplace must be a 5-character INSEE code'),
      excludeEmptyString: true,
    })
    .nullable();

// ---------------------------------------------------------------------------
// Composed schemas
// ---------------------------------------------------------------------------

/** Fields common to the first-call and the CR02-retry schemas. */
const baseTraitsShape = () => ({
  nomNaissance: nomNaissanceValidator(),
  prenoms: prenomsValidator(),
  sexe: sexeValidator(),
  dateNaissance: dateNaissanceValidator(),
});

/**
 * First-call schema — used by the search form before the initial
 * `searchInsByTraits`. `lieuNaissance` is intentionally NOT part of this schema:
 * it must not be sent on the first call (EF_INS10_05). If a caller carries the
 * field in form state, strip it from the payload on the first call.
 */
export const insiSearchTraitsSchema = () =>
  Yup.object().shape(baseTraitsShape());

/**
 * CR02-retry schema — same as the first-call schema, plus a REQUIRED
 * `lieuNaissance` (the disambiguator the PS adds after a CR02 ambiguous result).
 */
export const insiRetryTraitsSchema = () =>
  Yup.object().shape({
    ...baseTraitsShape(),
    lieuNaissance: lieuNaissanceValidator().required(
      i18n.t('Birthplace is required to refine the search')
    ),
  });

/**
 * Lenient schema where `lieuNaissance` is optional — handy if a single form
 * instance is reused across both calls and the call-site decides whether to
 * include the field in the payload.
 */
export const insiTraitsSchema = () =>
  Yup.object().shape({
    ...baseTraitsShape(),
    lieuNaissance: lieuNaissanceValidator(),
  });
